import React from "react";
import { Link } from "react-router-dom";
import { ArrowDownCircleIcon, ArrowUpCircleIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { ParitaWorkspace } from "@/common/user";
import { getCurrency } from "@/lib/utils";
import {
  SystemWorkspaceKPIValue as MetricValue,
  ValueMetric,
} from "@/common/system-workspace";

interface KPIProps {
  metrics: Record<string, MetricValue>;
  metricsLoading: boolean;
  currentWorkspace: ParitaWorkspace;
}

const KPI: React.FC<KPIProps> = ({
  metrics,
  metricsLoading,
  currentWorkspace,
}) => {
  const formatValue = (value: number, valueMetric?: ValueMetric) => {
    const currencySymbol = getCurrency(currentWorkspace, "symbol");
    const currencyISO = getCurrency(currentWorkspace, "iso");
    switch (valueMetric) {
      case "percentage":
        return `${Math.round(value)}%`;
      case "currency":
        return value >= 1000000
          ? `${currencySymbol}${(value / 1000000).toFixed(1)}M`
          : new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: currencyISO,
              maximumFractionDigits: 0,
            }).format(value);
      case "count":
        return Math.round(value).toLocaleString();
      case "years":
        return `${Math.round(value)} years`;
      case "days":
        return `${Math.round(value)} days`;
      case "round:1":
        return Math.round(value * 10) / 10;
      default:
        return Math.round(value);
    }
  };

  const renderLoadingSkeleton = () => (
    <div
      className={`grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-${
        Object.keys(metrics).length
      } gap-6`}
    >
      {[...Array(7)].map((_, index) => (
        <div
          key={index}
          className="bg-white rounded-lg shadow-md p-4 dark:bg-gray-800"
        >
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTrendIndicator = (metric: MetricValue) => {
    if (!metric.kpiTrend || metric.increase_is_good === undefined) return null;

    if (metric.value === metric.previousValue) return null;

    const isUp = metric.kpiTrend === "up";
    const colorClass =
      isUp === metric.increase_is_good ? "text-green-600" : "text-red-600";
    const Icon = isUp ? ArrowUpCircleIcon : ArrowDownCircleIcon;

    return <Icon className={`h-4 w-4 ${colorClass}`} />;
  };

  const renderTooltipContent = (metric: MetricValue) => {
    let contentData: Array<[string, React.ReactNode]> = [
      [
        `Last ${metric.periodLabel}`,
        formatValue(metric.value, metric.valueMetric as ValueMetric),
      ],
    ];
    if (metric.previousValue !== undefined) {
      contentData.push([
        `Previous ${metric.periodLabel}`,
        formatValue(metric.previousValue, metric.valueMetric as ValueMetric),
      ]);
    }

    if (
      metric.kpiName === "RECENT_ENGAGEMENT_SURVEY_SCORES" ||
      metric.kpiName === "LOWEST_SCORING_QUESTION_THEME" ||
      metric.kpiName === "HIGHEST_SCORING_QUESTION_THEME" ||
      metric.kpiName === "HIGHEST_SCORING_DEPARTMENT" ||
      metric.kpiName === "LOWEST_SCORING_DEPARTMENT"
    ) {
      contentData = [
        ["Most recent survey:", contentData[0][1]],
        ["Previous survey:", contentData[1]?.[1] ?? "N/A"],
      ];
    }

    const innerContent = contentData.map(([label, value]) => (
      <React.Fragment key={label}>
        <span className="text-gray-500 whitespace-nowrap text-left">
          {label}
        </span>
        <span className="text-right font-medium">{value}</span>
      </React.Fragment>
    ));

    return (
      <div className="flex flex-col gap-2">
        <div className="text-base font-medium flex items-center justify-center gap-2">
          {metric.title}
          {renderTrendIndicator(metric)}
        </div>
        <div className="h-px bg-gray-200 dark:bg-gray-700 -mx-2" />
        <div className="grid grid-cols-[auto,1fr] gap-x-4 text-sm">
          {innerContent}

          {metric.explanatoryText && (
            <span
              className="text-xs text-gray-400 dark:text-gray-500 mt-1 italic"
              dangerouslySetInnerHTML={{ __html: metric.explanatoryText }}
            />
          )}
        </div>
      </div>
    );
  };

  const renderKPICard = (key: string, metric: MetricValue) => {
    let additionalText = metric.additionalText;
    const row = metric.extendedOptions?.row;
    if (!metric.value) {
      return <React.Fragment key={key}></React.Fragment>;
    }
    if (
      metric.kpiName === "HIGHEST_SCORING_QUESTION_THEME" ||
      metric.kpiName === "LOWEST_SCORING_QUESTION_THEME"
    ) {
      additionalText =
        (row?.question_theme as string | undefined) ?? additionalText;
    }
    if (
      metric.kpiName === "HIGHEST_SCORING_DEPARTMENT" ||
      metric.kpiName === "LOWEST_SCORING_DEPARTMENT"
    ) {
      additionalText =
        (row?.department as string | undefined) ?? additionalText;
    }

    const formattedValueDiv = (
      <div className="text-2xl font-medium text-gray-900 dark:text-gray-100">
        {formatValue(metric.value, metric.valueMetric as ValueMetric)}
      </div>
    );

    return (
      <div
        key={key}
        className="bg-white rounded-lg border border-gray-200 p-3 text-center dark:bg-gray-800 dark:border-gray-700"
      >
        <div className="flex flex-col items-center">
          <div className="text-gray-500 text-sm font-normal mb-1 dark:text-gray-400">
            {metric.title}
          </div>
          <div className="flex items-center gap-1">
            {formattedValueDiv}
            {metric?.increase_is_good !== undefined && metric?.kpiTrend && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    {renderTrendIndicator(metric)}
                  </TooltipTrigger>
                  <TooltipContent className="text-left">
                    {renderTooltipContent(metric)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          {additionalText && (
            <span className="text-xs text-gray-600 dark:text-gray-500 mt-1">
              {additionalText}
            </span>
          )}
          {metric?.id && (
            <Link
              to={`/report/${metric?.id}`}
              className="text-xs text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 mt-1 italic"
            >
              Explore more
            </Link>
          )}
        </div>
      </div>
    );
  };

  if (metricsLoading) {
    return renderLoadingSkeleton();
  }
  // leave this comment so tailwind can pick up the dynamic class names for grid-cols
  // xl:grid-cols-4 xl:grid-cols-5 xl:grid-cols-6 xl:grid-cols-7 xl:grid-cols-8
  return (
    <div
      className={`grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-${
        Object.keys(metrics).length
      } gap-6`}
    >
      {Object.entries(metrics).map(([key, metric]) =>
        renderKPICard(key, metric),
      )}
    </div>
  );
};

export default KPI;
